import { apiClient, getApiBaseUrl } from './client.js';

export const pharmacyToolApi = {
  /**
   * Lấy danh sách 26 quy trình chuẩn từ Database
   */
  async getPharmacySops(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/sops', { params });
    return res.data;
  },

  /**
   * Lấy thông tin chi tiết 1 quy trình theo mã (QT-01 ...)
   */
  async getPharmacySopByCode(code) {
    const res = await apiClient.get(`/pharmacy-tool/sops/${code}`);
    return res.data;
  },

  /**
   * Trả về URL trực tiếp để tải file Word (.docx)
   */
  getDownloadSopUrl(code) {
    const base = getApiBaseUrl ? getApiBaseUrl() : '/api';
    return `${base.replace(/\/$/, '')}/pharmacy-tool/download/${code}`;
  },

  /**
   * Tải file docx chuẩn an toàn qua Blob (hoạt động 100% tin cậy trên cả Local, VPS Domain HTTPS và Nginx)
   * Giúp tránh lỗi trình duyệt chặn Mixed Content (khi web chạy https mà link tải http)
   */
  async downloadSopFile(code, fileName) {
    try {
      const blob = await apiClient.get(`/pharmacy-tool/download/${code}`, {
        responseType: 'blob',
      });

      const fileBlob =
        blob instanceof Blob
          ? blob
          : new Blob([blob], {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });

      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName || `${code}.docx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 2000);
      return true;
    } catch (error) {
      console.error(`Lỗi khi tải tài liệu quy trình ${code}:`, error);
      throw error;
    }
  },

  /**
   * Lấy cấu hình chẩn đoán quầy của người dùng
   */
  async getPharmacyConfig() {
    const res = await apiClient.get('/pharmacy-tool/config');
    return res.data;
  },

  /**
   * Lưu cấu hình chẩn đoán quầy lên server
   */
  async savePharmacyConfig(config) {
    const res = await apiClient.post('/pharmacy-tool/config', config);
    return res.data;
  },

  /**
   * Lấy tiến độ áp dụng quy trình (danh sách mã QT đã hoàn thành)
   */
  async getPharmacyProgress() {
    const res = await apiClient.get('/pharmacy-tool/progress');
    return res.data;
  },

  /**
   * Tích/bỏ tích áp dụng quy trình
   */
  async toggleSopProgress(sopCode, isCompleted) {
    const res = await apiClient.post('/pharmacy-tool/progress/toggle', {
      sopCode,
      isCompleted,
    });
    return res.data;
  },

  /**
   * Gửi thông tin đăng ký nhận tư vấn và mở khóa báo cáo sức khỏe quầy thuốc
   */
  async submitHealthCheckLead(leadData) {
    const res = await apiClient.post('/pharmacy-tool/health-check/lead', leadData);
    return res.data;
  },

  /**
   * Gửi kết quả trắc nghiệm và thông tin liên hệ nhận lộ trình đào tạo
   */
  async submitQuizLead(leadData) {
    const res = await apiClient.post('/pharmacy-tool/quiz/lead', leadData);
    return res.data;
  },

  /**
   * Lấy danh sách lead trắc nghiệm
   */
  async getQuizLeads(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/quiz/leads', { params });
    return res.data;
  },

  /**
   * Bắt đầu lượt thi: server bốc 15 câu (không kèm đáp án) và trả mã lượt làm bài (bắt buộc đăng nhập)
   * @returns {{ attemptToken, topicKey, timeLimitSeconds, questions }}
   */
  async startQuiz(topicKey) {
    const res = await apiClient.post('/pharmacy-tool/quiz/start', { topicKey });
    return res.data;
  },

  /**
   * Nộp bài: server chấm điểm, lưu kết quả, trả đáp án và lời giải của các câu vừa làm
   * @returns {{ id, score, rank, totalQuestions, totalCorrect, totalPartial, totalIncorrect, difficultyStats, subTopicStats, results }}
   */
  async submitQuiz({ attemptToken, answers, timeSpentSec }) {
    const res = await apiClient.post('/pharmacy-tool/quiz/submit', { attemptToken, answers, timeSpentSec });
    return res.data;
  },

  /**
   * Lấy lịch sử kết quả thi của người dùng đã đăng nhập
   */
  async getMyQuizResults() {
    const res = await apiClient.get('/pharmacy-tool/quiz/my-results');
    return res.data;
  },

  /**
   * Lấy danh sách câu hỏi trắc nghiệm từ database (không kèm đáp án)
   */
  async getQuizQuestions(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/quiz/questions', { params });
    return res.data;
  },

  /**
   * Gửi thông tin đăng ký nhận tư vấn và mở khóa tool cơ chế lương thưởng
   */
  async submitSalaryToolLead(leadData) {
    const res = await apiClient.post('/pharmacy-tool/salary/lead', leadData);
    return res.data;
  },

  /**
   * Lấy danh sách lead cơ chế lương thưởng (Dành cho Admin CMS)
   */
  async getSalaryToolLeads(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/salary/leads', { params });
    return res.data;
  },

  /**
   * Bóc tách chương trình khuyến mãi nhà cung cấp
   */
  async analyzePromotion(data) {
    const res = await apiClient.post('/pharmacy-tool/promotion/analyze', data);
    return res.data;
  },

  /**
   * Gửi thông tin đăng ký nhận tư vấn và tải báo cáo Giá Nhập Thật
   */
  async submitPromotionLead(leadData) {
    const res = await apiClient.post('/pharmacy-tool/promotion/lead', leadData);
    return res.data;
  },

  /**
   * Lấy danh sách lead công cụ Giá Nhập Thật (Dành cho Admin CMS)
   */
  async getPromotionLeads(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/promotion/leads', { params });
    return res.data;
  },

  /**
   * Gửi thông tin đăng ký nhận tư vấn và mở khóa kế hoạch Lên Chương Trình Bán Hàng
   */
  async submitSalesCampaignLead(leadData) {
    const res = await apiClient.post('/pharmacy-tool/sales-campaign/lead', leadData);
    return res.data;
  },

  /**
   * Lấy danh sách lead công cụ Lên Chương Trình Bán Hàng (Dành cho Admin CMS)
   */
  async getSalesCampaignLeads(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/sales-campaign/leads', { params });
    return res.data;
  },

  /**
   * Danh sách lead một công cụ cho trang quản lý lead (ADMIN).
   * source: 'health-check' | 'quiz' | 'salary' | 'promotion' | 'sales-campaign'
   * params: { page, limit (<=100), q, from, to (YYYY-MM-DD) }
   */
  async getToolLeads(source, params = {}) {
    const res = await apiClient.get(`/pharmacy-tool/${source}/leads`, { params });
    return res.data;
  },

  /**
   * Số lead của 5 công cụ (lọc from, to) — ADMIN
   */
  async getLeadCounts(params = {}) {
    const res = await apiClient.get('/pharmacy-tool/leads/counts', { params });
    return res.data;
  },

  /**
   * Link tải công cụ tạo video hàng loạt (bắt buộc đăng nhập)
   * @returns {{ url: string }}
   */
  async getVideoToolDownload() {
    const res = await apiClient.get('/pharmacy-tool/video-tool/download');
    return res.data;
  },

  /**
   * Link tải công cụ tạo video sức khỏe tay chỉ (bắt buộc đăng nhập)
   * @returns {{ url: string }}
   */
  async getHealthVideoToolDownload() {
    const res = await apiClient.get('/pharmacy-tool/health-video-tool/download');
    return res.data;
  },

  /** Link tải bộ skill tạo video hỏi đáp nhà thuốc (.zip gồm skill + hướng dẫn PDF) — cần đăng nhập và đã mua */
  async getQaVideoSkillDownload() {
    const res = await apiClient.get('/pharmacy-tool/qa-video-skill/download');
    return res.data;
  },

  /** Link tải bộ skill video kể chuyện quảng cáo (.zip gồm skill + hướng dẫn PDF) — cần đăng nhập và đã mua */
  async getStoryVideoSkillDownload() {
    const res = await apiClient.get('/pharmacy-tool/story-video-skill/download');
    return res.data;
  },

  /** Link thư mục Google Drive công cụ tạo video so sánh hàng loạt (trả { url, external: true }) — cần đăng nhập và đã mua */
  async getCompareVideoToolDownload() {
    const res = await apiClient.get('/pharmacy-tool/compare-video-tool/download');
    return res.data;
  },

  /** Link tải plugin ChatGPT video dược sĩ / chuyên gia chia sẻ (.zip) — cần đăng nhập và đã mua */
  async getExpertVideoSkillDownload() {
    const res = await apiClient.get('/pharmacy-tool/expert-video-skill/download');
    return res.data;
  },

  /** Link tải file hướng dẫn Word của plugin trên — cùng quyền */
  async getExpertVideoGuideDownload() {
    const res = await apiClient.get('/pharmacy-tool/expert-video-guide/download');
    return res.data;
  },

  /**
   * Quyền dùng từng công cụ theo gói trả phí của người đang xem (cấu hình ở back-end constants/tool-access.constant.js)
   * @returns {{ [slug: string]: { requiredPlans: Array<{ id, code, name, price, durationMonths }>, hasAccess: boolean } }}
   */
  async getToolAccess() {
    const res = await apiClient.get('/pharmacy-tool/access');
    return res.data;
  },
};


